import { CameraOverlayButton, IconWithTextComponent, MicrophoneOverlayButton, PedagogyInterventionBottomBar, ReactIcons } from "@kalyzee/escaet-ui";
import { MediaCaptureRef, useRefAutoUpdate, Video } from "@kalyzee/kast-web-sdk/web-components";
import { MeetingSessionHighlightingState } from "@kalyzee/kast-web-sdk/websocket";
import React, { createRef, useCallback, useEffect, useRef, useState } from "react";

import { MeetingContainerAPI } from "@kalyzee/kast-web-sdk/contexts/meeting/core/container";
import { removePopupByGroup } from "../../helpers/popup";
import { toastError } from "../../helpers/toast";
import { useMeetingSelector } from "../../hooks/meeting";
import { ExpertContextState } from "../../pages/master/expert/context";
import { PedagogyContextState } from "../../pages/master/pedagogy/context";
import { MediaSelector } from "../media/MediaSelector";
import { ScreenShare, ScreenShareStatus } from "../media/ScreenShare";
import SelfPreview from "../media/SelfPreview";
import OverlayInformationMessage from "../utils/OverlayInformationMessage";
import { pedagogyWebcamName } from "../../helpers/webcam";
import { useMediaContext } from "../../pages/master/common/private/mediaContext";

export interface PedagogyBottomBarProps {
  meetingContainerAPI: MeetingContainerAPI | undefined;
  context?: PedagogyContextState | ExpertContextState;
  backButtonText?: string;
  disableRemoteButton?: boolean;
  remoteBadgeValue?: number;
  onBackButton?: () => void;
  onRemoteController?: () => void;
}

export interface PedagogyBottomBarRef {}

export const PedagogyBottomBar = React.forwardRef(
  (
    { meetingContainerAPI, context, backButtonText = "Retour", disableRemoteButton, remoteBadgeValue, onBackButton, onRemoteController }: PedagogyBottomBarProps,
    forwardRef: React.ForwardedRef<PedagogyBottomBarRef>
  ) => {
    const mediaCtx = useMediaContext();
    const mediaCtxRef = useRefAutoUpdate(mediaCtx);

    const meetingContainerAPIRef = useRefAutoUpdate(meetingContainerAPI);

    const [session, sessionRef] = useMeetingSelector(meetingContainerAPI, (state) => state.session);

    const [emissionSessions] = useMeetingSelector(meetingContainerAPI, (state) => state.emissionSessions);

    const [selfVideoStream, setSelfVideoStream] = useState<MediaStream | undefined>(undefined);
    const [selfScreenStream, setSelfScreenStream] = useState<MediaStream | undefined>(undefined);
    const [popupMode, setPopupMode] = useState<"none" | "media" | "screen">("none");
    const [screenShareStatus, setScreenShareStatus] = useState<ScreenShareStatus>("idle");
    const mediaSelectorRef = useRef<MediaCaptureRef>();
    const popupContainerRef = useRef<HTMLDivElement>(null);
    const contextRef = useRefAutoUpdate(context);

    // ---------------------------------------------------------------------- //
    // ---------------------------   UTILS ----------------------------- //
    // ---------------------------------------------------------------------- //

    // ---------------------------------------------------------------------- //
    // ---------------------------   EFFECT ----------------------------- //
    // ---------------------------------------------------------------------- //

    useEffect(() => {
      return () => {
        removePopupByGroup("main");
      };
    }, []);


  useEffect(() => {
    const manager = meetingContainerAPI?.emissionManager;
    if (!manager) return;
    mediaCtxRef.current?.setVideoStreamEnabled(manager.videoIsEnabled("cameras"));
    mediaCtxRef.current?.setScreensVideoStreamEnabled(manager.videoIsEnabled("screens"));
    mediaCtxRef.current?.setAudioStreamEnabled(manager.audioIsEnabled("cameras"));
  }, [session, meetingContainerAPI?.emissionManager]);

    useEffect(() => {
      emissionSessions?.forEach((s) => {
        const type = s.data.mediaSession.extra?.type;
        const group = type === "camera" ? "cameras" : type === "screen" ? "screens" : "undefined";
        if (group) s.data.groups = [group];
      });
    }, [emissionSessions]);

    // ---------------------------------------------------------------------- //
    // ---------------------------   RENDER ----------------------------- //
    // ---------------------------------------------------------------------- //

    const renderLeftButtonBottomBar = () => {
      return (
        <IconWithTextComponent
          text={backButtonText}
          icon={ReactIcons.PedagoArrowLeft}
          style={{
            backgroundColor: "#777777",
            color: "white",
            padding: "1em 1.5em",
            gap: "0.5em",
            fontSize: "0.8em",
          }}
          onClick={() => onBackButton?.()}
        />
      );
    };

    const bindWebcamMedia = useCallback((mediaStream: MediaStream) => {
      setSelfVideoStream(mediaStream);
      const session = sessionRef.current;
      if (!session) return;
      const media = session.medias.find((m) => m.name === pedagogyWebcamName);
      if (!media) return;
      meetingContainerAPIRef.current?.setMediaStream(media.id, mediaStream);
    }, []);

    const renderAllSelfPreviews = () => {
      return (
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            gap: "0.5em",
          }}
        >
          {screenShareStatus !== "idle" && (
            <SelfPreview
              color={popupMode === "screen" ? "#888888" : "#555555"}
              stream={selfScreenStream}
              icon={
                <ReactIcons.SharedScreen
                  style={{
                    color: "#3BD2C9",
                    width: 28,
                    height: 28,
                  }}
                />
              }
              onClick={() => setPopupMode((v) => (v === "screen" ? "none" : "screen"))}
            />
          )}
          <SelfPreview
            color={popupMode === "media" ? "#888888" : "#555555"}
            stream={selfVideoStream}
            icon={<ReactIcons.Settings2 fill={popupMode === "media" ? "#CCCCCC" : "#999999"} width={25} height={25} />}
            onClick={() => setPopupMode((v) => (v === "media" ? "none" : "media"))}
          />
        </div>
      );
    };

    const renderUIBottomRightDock = () => {
      return (
        <div
          ref={popupContainerRef}
          style={{
            position: "absolute",
            right: 0,
            bottom: 0,
            zIndex: 5,
            margin: "1em",
          }}
        >
          {/* This component has to stay mounted */}

          <MediaSelector
            ref={mediaSelectorRef}
            display={popupMode === "media"}
            style={{
              width: "500px",
              height: "400px",
            }}
            quality={session?.highlighting === MeetingSessionHighlightingState.ONGOING ? "high" : "low"}
            audioStreamEnabled={mediaCtx?.audioStreamEnabled}
            videoStreamEnabled={mediaCtx?.videoStreamEnabled}
            onMediaStream={bindWebcamMedia}
            onMediaCaptureError={(type, error) => {
              if (type === "audio") {
                toastError("Veuillez autoriser le navigateur à accéder au microphone");
                meetingContainerAPIRef.current?.mute({ direction: "outgoing", group: "cameras" });
              }
              if (type === "video") {
                toastError("Veuillez autoriser le navigateur à accéder à la caméra");
                meetingContainerAPIRef.current?.disableVideo({ direction: "outgoing", group: "cameras" })
              }
            }}
          />

          {popupMode === "screen" ? (
            <div
              style={{
                width: 500,
                borderRadius: 6,
                padding: 4,
                backgroundColor: "#555555",
              }}
            >
              <Video autoPlay muted style={{ width: "100%", height: "100%", borderRadius: 6, objectFit: "contain" }} src={selfScreenStream} />
            </div>
          ) : null}
        </div>
      );
    };

    const renderCameraButton = () => {
      const permitted = !(session?.permissions?.video === false);
      const currRef = createRef<HTMLDivElement>();
      let hoverMessage: string | undefined = undefined;
      if (!permitted) hoverMessage = "Vous ne pouvez actuellement pas activer votre caméra";
      return (
        <div ref={currRef}>
          <CameraOverlayButton
            key="student-dashboard-overlay-camera-btn"
            off={!mediaCtx?.videoStreamEnabled}
            onClick={async () => {
              if (!meetingContainerAPIRef.current) return;
              const manager = meetingContainerAPIRef.current.emissionManager;
              let success = true;
              if (manager.videoIsEnabled("cameras")) {
                success = (await meetingContainerAPIRef.current.disableVideo({ direction: "outgoing", group: "cameras" })).success;
              } else {
                success = (await meetingContainerAPIRef.current.enableVideo({ direction: "outgoing", group: "cameras" })).success;
              }
              if (!success) toastError("Une erreur est survenue...");
            }}
            disabled={!permitted}
          />
          {hoverMessage ? <OverlayInformationMessage targetRef={currRef} message={hoverMessage} /> : null}
        </div>
      );
    };

    const renderAudioButton = () => {
      const permitted = !(session?.permissions?.audio === false);
      const currRef = createRef<HTMLDivElement>();
      let hoverMessage: string | undefined = undefined;
      if (!permitted) hoverMessage = "Vous ne pouvez actuellement pas activer votre micro";
      return (
        <div ref={currRef}>
          <MicrophoneOverlayButton
            key="student-dashboard-overlay-microphone-btn"
            off={!mediaCtx?.audioStreamEnabled}
            onClick={async () => {
              if (!meetingContainerAPIRef.current) return;
              const manager = meetingContainerAPIRef.current.emissionManager;
              let success = true;
              if (manager.audioIsEnabled("cameras")) {
                success = (await meetingContainerAPIRef.current.mute({ direction: "outgoing", group: "cameras" })).success;
              } else {
                success = (await meetingContainerAPIRef.current.unmute({ direction: "outgoing", group: "cameras" })).success;
              }
              if (!success) toastError("Une erreur est survenue...");
            }}
            disabled={!permitted}
          />
          {hoverMessage ? <OverlayInformationMessage targetRef={currRef} message={hoverMessage} /> : null}
        </div>
      );
    };

    const renderScreenShareButton = () => {
      return (
        <ScreenShare meetingContainerAPI={meetingContainerAPI} key="student-dashboard-overlay-screen-share-btn" onStatus={setScreenShareStatus} onStream={(stream) => setSelfScreenStream(stream)} />
      );
    };

    return (
      <PedagogyInterventionBottomBar
        forceOpen
        onRemoteButtonClick={onRemoteController}
        buttonsCenter={[renderCameraButton(), renderAudioButton(), renderScreenShareButton()]}
        rightPlaceholder={renderAllSelfPreviews()}
        dockedContentRight={renderUIBottomRightDock()}
        leftPlaceholder={renderLeftButtonBottomBar()}
        disableRemoteButton={disableRemoteButton}
        remoteBadgeValue={remoteBadgeValue}
      />
    );
  }
);
